import React, { useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { RetencaoMarcacao } from '../api/cccApi';
import {
  WORKSPACE_SECTIONS,
  WorkspaceSectionKey,
  normalizeWorkspaceSubstage,
  sectionForSubstage,
} from '../config/retencaoWorkspace';
import {
  isMarcacaoWorkspaceFormIncomplete,
  SPECIAL_TAB_SUBSTAGES_REQUIRING_COMPLETION,
} from '../config/fidelizadosDados';
import { RetencaoMotivoWarningIcon } from './RetencaoMotivoWarning';
import { getMarcacaoRowSummary, getWorkspaceSubstage } from '../utils/retencaoMarcacaoDisplay';
import RetencaoLeadPanel from './RetencaoLeadPanel';
import CccCalendarEventModal from './CccCalendarEventModal';
import {
  buildRetencaoCalendarClient,
  defaultCalendarEventTitle,
  defaultCalendarSlotTimes,
} from '../utils/calendarClientFromMarcacao';
import { parseCreatedAt } from '../utils/workspaceCreationDateFilter';
import '../styles/cccCalendar.css';

const ROWS_PER_PAGE = 10;

const marcacaoClaimedAtIso = (m: RetencaoMarcacao): string | undefined =>
  m.retencao_claim?.claimed_at ?? undefined;

const sortMarcacoesByNewestClaim = (a: RetencaoMarcacao, b: RetencaoMarcacao) => {
  const ta = parseCreatedAt(marcacaoClaimedAtIso(a))?.getTime() ?? 0;
  const tb = parseCreatedAt(marcacaoClaimedAtIso(b))?.getTime() ?? 0;
  return tb - ta;
};

export type WorkspaceFocusTarget = {
  marcacaoId: string;
  substage: string;
  infoTab?: 'historico';
};

type Props = {
  items: RetencaoMarcacao[];
  onSubstageChange: (marcacaoId: string, substage: string) => Promise<boolean>;
  onRetroceder: (marcacaoId: string) => void;
  onAddNote: (marcacaoId: string, text: string) => Promise<void>;
  onMarcacaoRefresh?: () => void | Promise<void>;
  workspaceFocus?: WorkspaceFocusTarget | null;
  onWorkspaceFocusDone?: () => void;
  bulkSelectEnabled?: boolean;
  bulkSelectedIds?: Set<string>;
  onBulkToggle?: (marcacaoId: string, checked: boolean) => void;
  onBulkTogglePage?: (marcacaoIds: string[], checked: boolean) => void;
};

const RetencaoWorkspaceTables: React.FC<Props> = ({
  items,
  onSubstageChange,
  onRetroceder,
  onAddNote,
  onMarcacaoRefresh,
  workspaceFocus,
  onWorkspaceFocusDone,
  bulkSelectEnabled = false,
  bulkSelectedIds,
  onBulkToggle,
  onBulkTogglePage,
}) => {
  const [activeSection, setActiveSection] = useState<WorkspaceSectionKey>('por_contactar');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [savingNote, setSavingNote] = useState(false);
  const [expandedSubsections, setExpandedSubsections] = useState<Record<string, boolean>>({});
  const [subsectionPages, setSubsectionPages] = useState<Record<string, number>>({});
  const [scrollTrigger, setScrollTrigger] = useState(0);
  const leadPanelRef = useRef<HTMLTableRowElement | null>(null);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [calendarMarcacao, setCalendarMarcacao] = useState<RetencaoMarcacao | null>(null);
  const [calendarSlot, setCalendarSlot] = useState(() => defaultCalendarSlotTimes());
  const [initialInfoTabForSelection, setInitialInfoTabForSelection] = useState<
    'historico' | undefined
  >();

  const openCreateCalendarEvent = (m: RetencaoMarcacao) => {
    setCalendarMarcacao(m);
    setCalendarSlot(defaultCalendarSlotTimes());
    setCalendarModalOpen(true);
  };

  const focusWorkspaceSubstage = (
    marcacaoId: string,
    substage: string,
    rowsForSubstage?: RetencaoMarcacao[]
  ) => {
    const normalized = normalizeWorkspaceSubstage(substage);
    const section = sectionForSubstage(normalized);
    if (section) setActiveSection(section.key);
    setExpandedSubsections((prev) => ({ ...prev, [normalized]: true }));
    if (rowsForSubstage) {
      const idx = rowsForSubstage.findIndex((m) => (m.marcacao_id || String(m.id)) === marcacaoId);
      if (idx >= 0) {
        setSubsectionPages((prev) => ({
          ...prev,
          [normalized]: Math.floor(idx / ROWS_PER_PAGE) + 1,
        }));
      }
    }
    setSelectedId(marcacaoId);
    setScrollTrigger((n) => n + 1);
    window.requestAnimationFrame(() => {
      const subsectionEl = document.querySelector(`[data-subsection="${normalized}"]`);
      subsectionEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const scrollLeadPanelIntoView = (rowEl: HTMLElement) => {
    const scrollRoot = rowEl.closest('.rec-desk-body') as HTMLElement | null;
    const panel = rowEl.querySelector('.rec-lead-panel') as HTMLElement | null;
    const target = panel || rowEl;

    if (!scrollRoot) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    const rootRect = scrollRoot.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const panelRect = panel?.getBoundingClientRect();
    const padding = 12;

    let nextTop = scrollRoot.scrollTop + (targetRect.top - rootRect.top) - padding;

    if (panel && panelRect && panelRect.bottom > rootRect.bottom - padding) {
      nextTop += panelRect.bottom - rootRect.bottom + padding;
    }

    scrollRoot.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
  };

  useEffect(() => {
    if (!selectedId) return;
    let frame2 = 0;
    const frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(() => {
        const row = leadPanelRef.current;
        if (row) scrollLeadPanelIntoView(row);
      });
    });
    return () => {
      window.cancelAnimationFrame(frame1);
      if (frame2) window.cancelAnimationFrame(frame2);
    };
  }, [selectedId, scrollTrigger]);

  useEffect(() => {
    if (!selectedId) return;
    const stillExists = items.some((m) => (m.marcacao_id || String(m.id)) === selectedId);
    if (!stillExists) setSelectedId(null);
  }, [items, selectedId]);

  const bySubstage = useMemo(() => {
    const map: Record<string, RetencaoMarcacao[]> = {};
    WORKSPACE_SECTIONS.forEach((sec) => {
      sec.subsections.forEach((sub) => {
        map[sub.substage] = [];
      });
    });
    items.forEach((m) => {
      const st = normalizeWorkspaceSubstage(getWorkspaceSubstage(m));
      if (map[st]) map[st].push(m);
      else map['por-contactar-iniciado-atribuido'].push(m);
    });
    Object.keys(map).forEach((key) => {
      map[key].sort(sortMarcacoesByNewestClaim);
    });
    return map;
  }, [items]);

  useEffect(() => {
    setSubsectionPages({});
  }, [items, activeSection]);

  useEffect(() => {
    if (!workspaceFocus) return;
    const { marcacaoId, substage, infoTab } = workspaceFocus;
    const normalized = normalizeWorkspaceSubstage(substage);
    const exists = items.some((m) => (m.marcacao_id || String(m.id)) === marcacaoId);
    if (!exists) return;

    if (infoTab === 'historico') {
      setInitialInfoTabForSelection('historico');
    }
    focusWorkspaceSubstage(marcacaoId, substage, bySubstage[normalized] || []);
    const doneTimer = window.setTimeout(() => onWorkspaceFocusDone?.(), 400);
    return () => window.clearTimeout(doneTimer);
  }, [workspaceFocus, items, bySubstage, onWorkspaceFocusDone]);

  useEffect(() => {
    if (!selectedId) setInitialInfoTabForSelection(undefined);
  }, [selectedId]);

  const setSubsectionPage = (substage: string, page: number) => {
    setSubsectionPages((prev) => ({ ...prev, [substage]: Math.max(1, page) }));
  };

  const sectionCounts = useMemo(() => {
    const counts: Record<WorkspaceSectionKey, number> = {
      por_contactar: 0,
      em_tratamento: 0,
      pre_fecho: 0,
      fecho: 0,
    };
    WORKSPACE_SECTIONS.forEach((sec) => {
      counts[sec.key] = sec.subsections.reduce(
        (n, sub) => n + (bySubstage[sub.substage]?.length || 0),
        0
      );
    });
    return counts;
  }, [bySubstage]);

  const activeConfig = WORKSPACE_SECTIONS.find((s) => s.key === activeSection)!;

  const toggleSubsection = (substage: string) => {
    setExpandedSubsections((prev) => ({ ...prev, [substage]: !prev[substage] }));
  };

  const redirectToRequiredCurrentLead = (marcacaoId: string, substage: string) => {
    focusWorkspaceSubstage(marcacaoId, substage);
    window.requestAnimationFrame(() => {
      const subsectionEl = document.querySelector(`[data-subsection="${substage}"]`);
      subsectionEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const canLeaveCurrentLead = () => {
    if (!selectedId) return true;
    const current = items.find((m) => (m.marcacao_id || String(m.id)) === selectedId);
    if (!current) return true;
    const currentSubstage = normalizeWorkspaceSubstage(getWorkspaceSubstage(current));
    if (!SPECIAL_TAB_SUBSTAGES_REQUIRING_COMPLETION.has(currentSubstage)) return true;
    const incomplete = isMarcacaoWorkspaceFormIncomplete(currentSubstage, current.retencao_claim);
    if (!incomplete) return true;
    redirectToRequiredCurrentLead(selectedId, currentSubstage);
    message.error('Preencha e guarde os campos obrigatórios deste separador antes de abrir outra marcação.');
    return false;
  };

  const openLead = (id: string, substage: string) => {
    if (selectedId && selectedId !== id && !canLeaveCurrentLead()) return;
    focusWorkspaceSubstage(id, substage);
  };

  const handleSubstageChangeForLead = async (marcacaoId: string, substage: string) => {
    const ok = await onSubstageChange(marcacaoId, substage);
    if (ok) focusWorkspaceSubstage(marcacaoId, substage);
  };

  const toggleLead = (id: string, substage: string) => {
    if (selectedId === id) {
      if (!canLeaveCurrentLead()) return;
      setSelectedId(null);
      return;
    }
    openLead(id, substage);
  };

  const renderTable = (rows: RetencaoMarcacao[]) => {
    if (rows.length === 0) {
      return <p className="rec-accordion-empty">Sem marcações neste estado.</p>;
    }
    const colCount = bulkSelectEnabled ? 7 : 6;
    const rowIds = rows.map((m) => m.marcacao_id || String(m.id));
    const pageAllSelected =
      bulkSelectEnabled &&
      rowIds.length > 0 &&
      rowIds.every((id) => bulkSelectedIds?.has(id));
    const pageSomeSelected =
      bulkSelectEnabled && rowIds.some((id) => bulkSelectedIds?.has(id)) && !pageAllSelected;

    return (
      <div className="rec-table-scroll">
        <table className="rec-table rec-table--selectable">
          <thead>
            <tr>
              {bulkSelectEnabled && (
                <th className="rec-table-col-bulk" scope="col">
                  <input
                    type="checkbox"
                    className="ccc-bulk-checkbox"
                    aria-label="Selecionar todos nesta página"
                    checked={pageAllSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = pageSomeSelected;
                    }}
                    onChange={(e) => onBulkTogglePage?.(rowIds, e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
              )}
              <th className="rec-table-col-chevron" aria-hidden />
              <th>Empresa / Responsável</th>
              <th>Produto</th>
              <th>Data e hora (Agendamento)</th>
              <th>Contacto</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => {
              const id = m.marcacao_id || String(m.id);
              const summary = getMarcacaoRowSummary(m);
              const isSelected = selectedId === id;
              const substage = normalizeWorkspaceSubstage(getWorkspaceSubstage(m));
              const formIncomplete = isMarcacaoWorkspaceFormIncomplete(
                substage,
                m.retencao_claim
              );
              const isBulkChecked = bulkSelectedIds?.has(id) ?? false;
              return (
                <React.Fragment key={id}>
                  <tr
                    className={`rec-table-row ${isSelected ? 'is-selected' : ''} ${
                      isBulkChecked ? 'is-bulk-selected' : ''
                    } ${formIncomplete ? 'rec-table-row--motivo-warn' : ''}`}
                    data-marcacao-id={id}
                    onClick={() => toggleLead(id, substage)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleLead(id, substage);
                      }
                    }}
                  >
                    {bulkSelectEnabled && (
                      <td className="rec-table-col-bulk" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="ccc-bulk-checkbox"
                          aria-label={`Selecionar ${summary.empresa}`}
                          checked={isBulkChecked}
                          onChange={(e) => onBulkToggle?.(id, e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                    )}
                    <td className="rec-table-col-chevron">
                      <span className={`rec-row-chevron ${isSelected ? 'is-open' : ''}`}>▶</span>
                    </td>
                    <td className="rec-table-cell-empresa">
                      <span className="rec-table-empresa-line">
                        {formIncomplete && <RetencaoMotivoWarningIcon />}
                        <span className="rec-table-empresa">{summary.empresa}</span>
                      </span>
                      <span className="rec-table-responsavel">{summary.responsavel}</span>
                    </td>
                    <td>
                      <span className="rec-table-produto">{summary.produto}</span>
                    </td>
                    <td>
                      <span className="rec-table-schedule">{summary.schedule}</span>
                    </td>
                    <td>
                      <span className="rec-table-contacto">{summary.contacto}</span>
                      {summary.email && (
                        <span className="rec-table-extra">{summary.email}</span>
                      )}
                    </td>
                    <td>
                      <span className="rec-table-estado-pill">{summary.estado}</span>
                    </td>
                  </tr>
                  {isSelected && (
                    <tr ref={leadPanelRef} className="rec-table-detail-row">
                      <td colSpan={colCount}>
                        <RetencaoLeadPanel
                          marcacao={m}
                          onCollapse={() => setSelectedId(null)}
                          onSubstageChange={(st) => handleSubstageChangeForLead(id, st)}
                          onWorkspaceNavigate={(st) => focusWorkspaceSubstage(id, st)}
                          onRetroceder={() => onRetroceder(id)}
                          savingNote={savingNote}
                          onAddNote={async (text) => {
                            setSavingNote(true);
                            try {
                              await onAddNote(id, text);
                            } finally {
                              setSavingNote(false);
                            }
                          }}
                          onMarcacaoRefresh={onMarcacaoRefresh}
                          onCreateCalendarEvent={() => openCreateCalendarEvent(m)}
                          initialInfoTab={
                            initialInfoTabForSelection === 'historico' ? 'historico' : undefined
                          }
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="rec-ws">
      <nav className="rec-ws-tabs" aria-label="Secções da área de trabalho">
        {WORKSPACE_SECTIONS.map((section) => (
          <button
            key={section.key}
            type="button"
            className={`rec-ws-tab ${activeSection === section.key ? 'is-active' : ''}`}
            onClick={() => setActiveSection(section.key)}
          >
            <span className="rec-ws-tab-dot" style={{ background: section.accent }} />
            {section.title}
            <span className="rec-ws-tab-count">{sectionCounts[section.key]}</span>
          </button>
        ))}
      </nav>

      <div className="rec-accordions">
        {activeConfig.subsections.map((sub) => {
          const subRows = bySubstage[sub.substage] || [];
          const totalRows = subRows.length;
          const totalPages = Math.max(1, Math.ceil(totalRows / ROWS_PER_PAGE));
          const requestedPage = subsectionPages[sub.substage] || 1;
          const currentPage = Math.min(Math.max(1, requestedPage), totalPages);
          const pageStart = totalRows === 0 ? 0 : (currentPage - 1) * ROWS_PER_PAGE + 1;
          const pageEnd = Math.min(currentPage * ROWS_PER_PAGE, totalRows);
          const paginatedRows = subRows.slice(
            (currentPage - 1) * ROWS_PER_PAGE,
            currentPage * ROWS_PER_PAGE
          );
          const isOpen = expandedSubsections[sub.substage] === true;
          const formIncompleteCount = SPECIAL_TAB_SUBSTAGES_REQUIRING_COMPLETION.has(sub.substage)
            ? subRows.filter((m) =>
                isMarcacaoWorkspaceFormIncomplete(
                  normalizeWorkspaceSubstage(getWorkspaceSubstage(m)),
                  m.retencao_claim
                )
              ).length
            : 0;
          return (
            <section
              key={sub.substage}
              className={`rec-accordion ${isOpen ? 'is-open' : ''} ${
                formIncompleteCount > 0 ? 'rec-accordion--motivo-warn' : ''
              }`}
              data-subsection={sub.substage}
            >
              <button
                type="button"
                className="rec-accordion-trigger"
                onClick={() => toggleSubsection(sub.substage)}
                aria-expanded={isOpen}
              >
                <span className="rec-accordion-chevron">▼</span>
                <span>{sub.label}</span>
                {formIncompleteCount > 0 && (
                  <span className="rec-accordion-warn" title="Marcações com formulário em falta">
                    <RetencaoMotivoWarningIcon />
                    <span className="rec-accordion-warn-count">{formIncompleteCount}</span>
                  </span>
                )}
                <span className="rec-accordion-count">{totalRows}</span>
              </button>
              {isOpen && (
                <div className="rec-accordion-body">
                  {renderTable(paginatedRows)}
                  {totalRows > ROWS_PER_PAGE && (
                    <nav
                      className="prosp-pagination"
                      aria-label={`Paginação — ${sub.label}`}
                    >
                      <button
                        type="button"
                        className="rec-btn rec-btn-ghost prosp-pagination-btn"
                        disabled={currentPage <= 1}
                        onClick={() => setSubsectionPage(sub.substage, currentPage - 1)}
                      >
                        Anterior
                      </button>
                      <span className="prosp-pagination-meta">
                        A mostrar {pageStart}–{pageEnd} de {totalRows} · Página {currentPage} de{' '}
                        {totalPages}
                      </span>
                      <button
                        type="button"
                        className="rec-btn rec-btn-ghost prosp-pagination-btn"
                        disabled={currentPage >= totalPages}
                        onClick={() => setSubsectionPage(sub.substage, currentPage + 1)}
                      >
                        Seguinte
                      </button>
                    </nav>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>

      <CccCalendarEventModal
        open={calendarModalOpen}
        initialStart={calendarSlot.start}
        initialEnd={calendarSlot.end}
        initialClient={calendarMarcacao ? buildRetencaoCalendarClient(calendarMarcacao) : null}
        lockClient={!!calendarMarcacao}
        defaultTitle={calendarMarcacao ? defaultCalendarEventTitle(calendarMarcacao) : ''}
        onClose={() => {
          setCalendarModalOpen(false);
          setCalendarMarcacao(null);
        }}
        onSaved={() => {
          setCalendarModalOpen(false);
          setCalendarMarcacao(null);
        }}
      />
    </div>
  );
};

export default RetencaoWorkspaceTables;
